<template>
  <section class="space-y-4">
    <header class="flex items-start justify-between gap-3">
      <div class="min-w-0 flex-1">
        <h3 class="text-base font-semibold text-slate-900">月度客户</h3>
        <p class="mt-1 text-sm text-slate-500">维护客户主档，并为当前月份配置开票额度、利润率和名单区域。</p>
      </div>
      <button type="button" class="btn-primary shrink-0 whitespace-nowrap" v-permission="'enterprise-invoice:customer:create'" :disabled="syncing" @click="$emit('create-master')">
        新增客户
      </button>
    </header>

    <div class="rounded-lg border border-slate-200 bg-slate-50 p-3">
      <div class="grid grid-cols-[repeat(auto-fit,minmax(220px,1fr))] gap-3">
        <label class="block min-w-0">
          <span class="filter-label">关键字</span>
          <input
            :value="keyword"
            type="search"
            class="form-input form-control-sm mt-1 w-full"
            placeholder="搜索公司或注册地"
            @input="$emit('update:keyword', $event.target.value)"
            @keydown.enter="$emit('search')"
          >
        </label>
        <label class="block min-w-0">
          <span class="filter-label">本月状态</span>
          <select
            :value="joinedFilter"
            class="form-select-native form-control-sm mt-1 w-full"
            @change="$emit('update:joined-filter', $event.target.value)"
          >
            <option value="all">全部</option>
            <option value="joined">已加入本月</option>
            <option value="notJoined">未加入本月</option>
          </select>
        </label>
        <label class="block min-w-0">
          <span class="filter-label">规划师</span>
          <UserSelector
            :model-value="plannerUserId"
            class="selector-compact mt-1"
            :multiple="false"
            placeholder="选择规划师"
            @update:modelValue="$emit('update:planner-user-id', $event)"
          />
        </label>
        <label class="block min-w-0">
          <span class="filter-label">财务专员</span>
          <UserSelector
            :model-value="financeUserId"
            class="selector-compact mt-1"
            :multiple="false"
            placeholder="选择财务专员"
            @update:modelValue="$emit('update:finance-user-id', $event)"
          />
        </label>
        <label class="block min-w-0">
          <span class="filter-label">渠道来源</span>
          <input
            :value="channelSource"
            type="search"
            class="form-input form-control-sm mt-1 w-full"
            placeholder="输入渠道"
            @input="$emit('update:channel-source', $event.target.value)"
            @keydown.enter="$emit('search')"
          >
        </label>
        <div class="flex items-end min-w-0">
          <button type="button" class="btn-secondary h-9 w-full whitespace-nowrap" :disabled="syncing || loading" @click="$emit('search')">
            查询
          </button>
        </div>
      </div>
    </div>

    <div class="overflow-hidden rounded-lg border border-slate-200">
      <div ref="customerTableScrollRef" class="overflow-x-auto">
        <table class="min-w-[1280px] w-full divide-y divide-slate-200 text-sm">
          <thead class="bg-slate-50 text-left text-xs font-semibold text-slate-500">
            <tr>
              <th class="data-table-sticky-left data-table-sticky-head data-table-sticky-identity whitespace-nowrap px-4 py-3">公司名称</th>
              <th class="whitespace-nowrap px-4 py-3">注册地</th>
              <th class="whitespace-nowrap px-4 py-3">财务专员</th>
              <th class="whitespace-nowrap px-4 py-3">规划师</th>
              <th class="whitespace-nowrap px-4 py-3">渠道来源</th>
              <th class="whitespace-nowrap px-4 py-3">本月状态</th>
              <th class="whitespace-nowrap px-4 py-3 text-right">本月开票额度</th>
              <th class="whitespace-nowrap px-4 py-3 text-right">本月利润率</th>
              <th class="whitespace-nowrap px-4 py-3">进项品名</th>
              <th class="whitespace-nowrap px-4 py-3">销项品名</th>
              <th class="whitespace-nowrap px-4 py-3">名单区域</th>
              <th class="data-table-sticky-right data-table-sticky-head data-table-sticky-action whitespace-nowrap px-4 py-3 text-right">操作</th>
            </tr>
          </thead>
          <tbody class="divide-y divide-slate-100 bg-white">
            <tr v-if="loading">
              <td colspan="12" class="px-4 py-10 text-center text-slate-500">加载中...</td>
            </tr>
            <tr v-else-if="rows.length === 0">
              <td colspan="12" class="px-4 py-10 text-center text-slate-500">暂无客户</td>
            </tr>
            <tr v-for="row in rows" v-else :key="row.customerId" class="hover:bg-slate-50">
              <td class="data-table-sticky-left data-table-sticky-identity px-4 py-3">
                <div class="font-medium text-slate-900">{{ row.companyName || '-' }}</div>
                <div
                  v-if="row.companyNameSnapshot && row.companyNameSnapshot !== row.companyName"
                  class="mt-1 text-xs text-amber-600"
                >
                  本月快照：{{ row.companyNameSnapshot }}
                </div>
                <div v-if="customerTagsOf(row).length > 0" class="mt-1 flex max-w-[260px] flex-wrap gap-1">
                  <span
                    v-for="tag in customerTagsOf(row)"
                    :key="tag.id"
                    class="data-table-inline-tag"
                  >
                    {{ tag.tagName }}
                  </span>
                </div>
              </td>
              <td class="whitespace-nowrap px-4 py-3 text-slate-600">
                {{ row.registeredRegion || '-' }}
              </td>
              <td class="whitespace-nowrap px-4 py-3 text-slate-600">
                {{ row.financeUserName || '-' }}
              </td>
              <td class="whitespace-nowrap px-4 py-3 text-slate-600">
                {{ row.plannerUserName || '-' }}
              </td>
              <td class="whitespace-nowrap px-4 py-3 text-slate-600">
                {{ row.channelSource || '-' }}
              </td>
              <td class="whitespace-nowrap px-4 py-3">
                <span :class="statusClass(row)">
                  {{ row.joinedCurrentMonth ? '已加入本月' : '未加入本月' }}
                </span>
              </td>
              <td class="whitespace-nowrap px-4 py-3 text-right tabular-nums text-slate-700">
                {{ formatAmount(row.targetOutputAmount) }}
              </td>
              <td class="whitespace-nowrap px-4 py-3 text-right tabular-nums text-slate-700">
                {{ formatRate(row.targetProfitRate) }}
              </td>
              <td class="max-w-[180px] px-4 py-3 text-slate-600">
                <span
                  class="line-clamp-2"
                  :title="row.joinedCurrentMonth ? formatProductQuotaSummary(row.inputProductQuotas).fullText : ''"
                >{{ row.joinedCurrentMonth ? formatProductQuotaSummary(row.inputProductQuotas).shortText : '-' }}</span>
              </td>
              <td class="max-w-[180px] px-4 py-3 text-slate-600">
                <span
                  class="line-clamp-2"
                  :title="row.joinedCurrentMonth ? formatProductQuotaSummary(row.outputProductQuotas).fullText : ''"
                >{{ row.joinedCurrentMonth ? formatProductQuotaSummary(row.outputProductQuotas).shortText : '-' }}</span>
              </td>
              <td class="whitespace-nowrap px-4 py-3 text-slate-600">
                {{ row.regionName || '-' }}
                <div v-if="row.joinedCurrentMonth" class="mt-1 space-y-0.5 text-xs text-slate-500">
                  <div :title="downstreamSummaryText(row)">{{ downstreamSummaryText(row) }}</div>
                  <div :title="preferenceSummaryText(row)">{{ preferenceSummaryText(row) }}</div>
                </div>
              </td>
              <td class="data-table-sticky-right data-table-sticky-action px-4 py-3">
                <div class="flex justify-end gap-2 whitespace-nowrap">
                  <button
                    type="button"
                    class="table-action-button is-primary"
                    v-permission="'enterprise-invoice:customer:update'"
                    title="编辑主档"
                    aria-label="编辑主档"
                    :disabled="syncing"
                    @click="$emit('edit-master', row)"
                  >
                    <PencilSquareIcon class="h-3.5 w-3.5" />
                  </button>
                  <button
                    v-if="row.joinedCurrentMonth"
                    type="button"
                    class="table-action-button is-primary"
                    v-permission="'enterprise-invoice:plan-customer:manage'"
                    title="编辑开票"
                    aria-label="编辑开票"
                    :disabled="syncing"
                    @click="$emit('edit-month', row)"
                  >
                    <DocumentTextIcon class="h-3.5 w-3.5" />
                  </button>
                  <button
                    v-else
                    type="button"
                    class="table-action-button is-primary"
                    v-permission="'enterprise-invoice:plan-customer:manage'"
                    title="加入本月"
                    aria-label="加入本月"
                    :disabled="syncing"
                    @click="$emit('join-month', row)"
                  >
                    <PlusCircleIcon class="h-3.5 w-3.5" />
                  </button>
                  <button
                    v-if="row.joinedCurrentMonth"
                    type="button"
                    class="table-action-button is-danger"
                    v-permission="'enterprise-invoice:plan-customer:manage'"
                    title="移除本月"
                    aria-label="移除本月"
                    :disabled="syncing"
                    @click="$emit('remove-month', row)"
                  >
                    <MinusCircleIcon class="h-3.5 w-3.5" />
                  </button>
                  <button
                    v-else
                    type="button"
                    class="table-action-button is-danger"
                    v-permission="'enterprise-invoice:customer:delete'"
                    title="删除主档"
                    aria-label="删除主档"
                    :disabled="syncing"
                    @click="$emit('delete-master', row)"
                  >
                    <TrashIcon class="h-3.5 w-3.5" />
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <AppPagination
      :current="page"
      :size="size"
      :total="total"
      :page-sizes="[10, 20, 50, 100]"
      @page-change="$emit('page-change', $event)"
      @size-change="$emit('size-change', $event)"
    />
  </section>
</template>

<script setup>
import {
  DocumentTextIcon,
  MinusCircleIcon,
  PencilSquareIcon,
  PlusCircleIcon,
  TrashIcon
} from '@heroicons/vue/24/outline'
import AppPagination from '@/components/AppPagination.vue'
import UserSelector from '@/components/UserSelector/index.vue'
import { formatProductQuotaSummary } from '../productQuota'
import { useWheelToHorizontalScroll } from '../composables/useWheelToHorizontalScroll'
import {
  downstreamAllocationSummary,
  downstreamPreferenceSummary
} from '../downstreamRules.mjs'

const props = defineProps({
  rows: { type: Array, default: () => [] },
  loading: { type: Boolean, default: false },
  syncing: { type: Boolean, default: false },
  keyword: { type: String, default: '' },
  joinedFilter: { type: String, default: 'all' },
  plannerUserId: { type: [Number, String], default: null },
  financeUserId: { type: [Number, String], default: null },
  channelSource: { type: String, default: '' },
  customerTags: { type: Array, default: () => [] },
  downstreamAllocations: { type: Array, default: () => [] },
  downstreamTagPreferences: { type: Array, default: () => [] },
  page: { type: Number, default: 1 },
  size: { type: Number, default: 20 },
  total: { type: Number, default: 0 }
})

defineEmits([
  'update:keyword',
  'update:joined-filter',
  'update:planner-user-id',
  'update:finance-user-id',
  'update:channel-source',
  'search',
  'page-change',
  'size-change',
  'create-master',
  'edit-master',
  'delete-master',
  'join-month',
  'edit-month',
  'remove-month'
])

const customerTableScrollRef = useWheelToHorizontalScroll()

function statusClass(row) {
  return [
    'inline-flex rounded-full px-2 py-0.5 text-xs font-medium',
    row.joinedCurrentMonth ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-600'
  ]
}

function formatAmount(value) {
  if (value === null || value === undefined || value === '') return '-'
  const amount = Number(value)
  if (!Number.isFinite(amount)) return '-'
  return amount.toLocaleString('zh-CN', { maximumFractionDigits: 0 })
}

function formatRate(value) {
  if (value === null || value === undefined || value === '') return '-'
  const rate = Number(value)
  if (!Number.isFinite(rate)) return '-'
  return `${(rate * 100).toFixed(2)}%`
}

function planCustomerOf(row) {
  return {
    ...row,
    id: row.planCustomerId,
    companyNameSnapshot: row.companyNameSnapshot || row.companyName,
    regionBucketId: row.regionBucketId,
    targetOutputAmount: row.targetOutputAmount,
    targetProfitRate: row.targetProfitRate
  }
}

function customerTagsOf(row) {
  if (Array.isArray(row.customerTags)) return row.customerTags
  if (Array.isArray(row.tags)) return row.tags
  if (Array.isArray(row.tagIds)) {
    const selected = new Set(row.tagIds.map(id => String(id)))
    return props.customerTags.filter(tag => selected.has(String(tag.id)))
  }
  return []
}

function downstreamSummaryText(row) {
  if (!row.joinedCurrentMonth) return ''
  const summary = downstreamAllocationSummary(props.downstreamAllocations, planCustomerOf(row))
  if (summary.enabledCount === 0) return '未指定下游'
  return `已指定 ${summary.enabledCount} 个下游，合计 ${formatAmount(summary.totalAmount)}`
}

function preferenceSummaryText(row) {
  if (!row.joinedCurrentMonth) return ''
  const summary = downstreamPreferenceSummary(props.downstreamTagPreferences, row.planCustomerId)
  return summary.enabledCount === 0 ? '未设置标签偏好' : summary.shortText
}
</script>

<style scoped>
.filter-label {
  display: block;
  font-size: 12px;
  font-weight: 500;
  line-height: 1.25rem;
  color: #64748b;
}
</style>
